import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Leaf, FlaskConical, Globe2 } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Common";
import Seo from "../components/Seo";
import { IMAGES, HERO_STATS, COLLECTIONS, HOUSE_BRANDS } from "../mock/mock";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is oud (agarwood)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oud, or agarwood, is a rare resinous heartwood formed within the Aquilaria tree. Prized for centuries in perfumery, incense and traditional medicine, it is among the most precious natural raw materials in the world.",
      },
    },
    {
      "@type": "Question",
      name: "What oud products does Meilleur Oudh & Aromas offer?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We offer graded agarwood oud chips (Muri, Salla, Jurra, Kalagashi, Girmit), pure oud oil (Grade A, Grade B and Boya), hand-blended bakhoor, oud jewellery and carved showpieces — all grown and distilled on our own plantations in North East India.",
      },
    },
    {
      "@type": "Question",
      name: "Where is Meilleur oud grown and does it ship worldwide?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Our oud is grown across 103 acres of Aquilaria plantations in Assam, Tripura and Nagaland, and distilled in 27 in-house units. We trade with partners across six GCC countries and Europe, with worldwide shipping available.",
      },
    },
  ],
};

const Home = () => {
  return (
    <div className="bg-noir">
      <Seo
        title="Meilleur Oudh & Aromas — Premium Agarwood Oud Chips, Oud Oil & Bakhoor"
        description="Premium agarwood oud chips, pure oud oil and hand-blended bakhoor, grown and distilled on our own 103-acre plantations in North East India. Worldwide oud trade & export."
        path="/"
        keywords="oud, agarwood, oud chips, oud oil, bakhoor, oud supplier India, agarwood exporter, buy oud oil, premium oud"
        jsonLd={homeJsonLd}
      />
      {/* HERO — clean split */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="eyebrow mb-7 flex items-center gap-4">
                <span className="inline-block w-12 h-px bg-[var(--green)]" />
                The House of Meilleur · Est. Assam
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-serif-display text-[3.4rem] md:text-[5rem] lg:text-[5.6rem] leading-[0.98] tracking-tight text-[var(--green-deep)]">
                The Soul of <span className="gold-text italic">Oud</span>, From Tree to Trade.
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-8 max-w-xl text-[var(--muted)] text-lg leading-relaxed font-body font-light">
                Agarwood chips, precious oud oil and hand-blended bakhoor — grown, distilled and crafted on our own
                103-acre plantations in North East India.
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link to="/oud-chips" className="btn-gold">Explore Collections <ArrowRight size={15} /></Link>
                <Link to="/about" className="btn-ghost">Our Story</Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={200}>
              <div className="relative">
                <div className="img-zoom">
                  <img src={IMAGES.oudChips} alt="Premium agarwood oud chips" className="w-full h-[420px] md:h-[540px] object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-[var(--green)] on-green px-8 py-6 rounded hidden md:block shadow-xl">
                  <div className="font-serif-display text-4xl text-[#e7dcb8]">103</div>
                  <div className="text-[0.6rem] tracking-[0.3em] uppercase mt-1 text-[#cfc3a0]">Acres of Aquilaria</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* stats strip */}
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 mt-16 md:mt-24">
          <div className="hairline opacity-40 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {HERO_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 90}>
                <div>
                  <div className="font-serif-display text-4xl md:text-5xl gold-text">{s.value}</div>
                  <div className="text-[0.68rem] tracking-[0.2em] uppercase text-[var(--muted)] mt-2">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal><SectionHeading eyebrow="Who We Are" title="One of the world's most" accent="precious woods" /></Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-[var(--muted)] text-lg leading-relaxed font-body font-light max-w-2xl">
                Agarwood — or oud — is a resinous heartwood formed within the Aquilaria tree, prized for centuries across
                the Middle East, South and South East Asia. Meilleur Oudh &amp; Aromas brings its cultivation under a
                sustainable, scientific and community-owned model.
              </p>
              <p className="mt-6 text-[var(--muted)] text-lg leading-relaxed font-body font-light max-w-2xl">
                From sapling to plantation, from distillation to finished fragrance — we are vertically integrated, and
                every expression traces back to the same forest.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 grid sm:grid-cols-3 gap-6">
                {[
                  { icon: Leaf, t: "Sustainable", d: "Farmer-first forestry" },
                  { icon: FlaskConical, t: "In-House", d: "27 distillation units" },
                  { icon: Globe2, t: "Global", d: "6 GCC trade nations" },
                ].map((f) => (
                  <div key={f.t} className="border-l-2 border-[var(--green)] pl-5">
                    <f.icon size={22} className="text-[var(--green)] mb-3" />
                    <div className="font-serif-display text-xl text-[var(--green-deep)]">{f.t}</div>
                    <div className="text-sm text-[var(--muted)] font-body">{f.d}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={160}>
              <div className="img-zoom">
                <img src={IMAGES.forest} alt="Aquilaria plantation" className="w-full h-[540px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="py-24 md:py-32 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="The Collections" title="From the" accent="plantation" align="center" /></Reveal>
          <Reveal delay={120}>
            <p className="text-center text-[var(--muted)] mt-6 max-w-xl mx-auto font-body font-light">
              Four expressions of the same heartwood — for the connoisseur, the home and the trade partner.
            </p>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            {COLLECTIONS.map((c, i) => (
              <Reveal key={c.slug} delay={i * 110}>
                <Link to={`/${c.slug}`} className="lux-card block h-full group overflow-hidden">
                  <div className="img-zoom h-64 rounded-none">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7">
                    <div className="eyebrow text-[0.6rem] mb-3">{c.kicker}</div>
                    <h3 className="font-serif-display text-2xl mb-3 group-hover:text-[var(--green)] transition-colors">{c.name}</h3>
                    <p className="text-sm text-[var(--muted)] leading-relaxed font-body">{c.desc}</p>
                    <div className="mt-5 flex items-center gap-2 text-[var(--green)] text-xs tracking-[0.2em] uppercase">
                      Discover <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE — solid bottle green */}
      <section className="py-32 bg-green on-green">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <div className="mb-8 flex items-center justify-center gap-3 text-[0.7rem] tracking-[0.4em] uppercase text-[#cfc3a0]">
              <span className="w-8 h-px bg-[#cfc3a0]" /> Company Profile, 2026
            </div>
            <p className="font-serif-display text-3xl md:text-[2.9rem] leading-[1.25] italic text-white">
              "Meilleur offers oud plantations across <span className="text-[#e7dcb8]">103 acres</span> of land and
              <span className="text-[#e7dcb8]"> 27 distillation units</span> in India, with its first-ever oud
              experience lounge in Delhi NCR."
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOUSE PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <Reveal><SectionHeading eyebrow="One House, Four Brands" title="The House of" accent="Meilleur" /></Reveal>
            <Reveal delay={120}>
              <Link to="/house" className="btn-ghost self-start">Explore The House <ArrowRight size={15} /></Link>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-2">
            {HOUSE_BRANDS.map((b, i) => (
              <Reveal key={b.num} delay={i * 90}>
                <div className="group flex gap-7 py-8 border-b border-[var(--line)]">
                  <span className="font-serif-display text-5xl gold-text opacity-50 group-hover:opacity-100 transition-opacity">{b.num}</span>
                  <div>
                    <h3 className="font-serif-display text-2xl mb-2 group-hover:text-[var(--green)] transition-colors">{b.name}</h3>
                    <p className="text-sm text-[var(--muted)] leading-relaxed font-body max-w-md">{b.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="bg-green on-green rounded px-8 py-20 md:py-24 text-center">
              <div className="mb-6 text-[0.7rem] tracking-[0.4em] uppercase text-[#cfc3a0]">Trade &amp; Partnerships</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] max-w-3xl mx-auto text-white">
                Bring the finest <span className="italic text-[#e7dcb8]">oud</span> to your market.
              </h2>
              <p className="mt-6 text-[#d7cfbb] max-w-xl mx-auto font-body font-light">
                We trade with 14 partners across 6 GCC countries, with distribution reaching into Europe. Let's build
                the next chapter together.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-10 bg-[#e7dcb8] text-[var(--green-deep)] px-9 py-4 text-xs tracking-[0.18em] uppercase font-medium rounded-sm hover:bg-white transition-colors">
                Start a Conversation <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
